
import { useState, useEffect } from 'react';
import { Database } from '@/integrations/supabase/types';

type ContentStatus = Database['public']['Enums']['content_status'];

export interface BlogPostFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string;
  status: ContentStatus;
  // SEO fields
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  // Scheduling field
  scheduled_at: string | null;
}

export function useBlogPostForm(initialData?: Partial<BlogPostFormData>) {
  const [formData, setFormData] = useState<BlogPostFormData>({
    title: initialData?.title || '',
    slug: initialData?.slug || '',
    excerpt: initialData?.excerpt || '',
    content: initialData?.content || '',
    featured_image: initialData?.featured_image || '',
    status: initialData?.status || 'draft',
    // Initialize SEO fields
    seo_title: initialData?.seo_title || '',
    seo_description: initialData?.seo_description || '',
    seo_keywords: initialData?.seo_keywords || '',
    // Initialize scheduling field
    scheduled_at: initialData?.scheduled_at || null,
  });
  
  const [initialFormData, setInitialFormData] = useState<BlogPostFormData | null>(null);
  
  // Simple flag to explicitly track content changes
  const [contentEdited, setContentEdited] = useState(false);
  
  // Set initial form data for comparison
  useEffect(() => {
    if (initialData && !initialFormData) {
      const initialFormState = {
        title: initialData.title || '',
        slug: initialData.slug || '',
        excerpt: initialData.excerpt || '',
        content: initialData.content || '',
        featured_image: initialData.featured_image || '',
        status: initialData.status || 'draft',
        seo_title: initialData.seo_title || '',
        seo_description: initialData.seo_description || '',
        seo_keywords: initialData.seo_keywords || '',
        scheduled_at: initialData.scheduled_at || null,
      };
      setInitialFormData(initialFormState);
      console.log("Initial form data set:", initialFormState);
      
      // Always reset the content edited flag when loading new data
      setContentEdited(false);
    }
  }, [initialData]);
  
  // For debugging: Log when form data changes
  useEffect(() => {
    console.log("Form data updated:", {
      title: formData.title.substring(0, 30) + (formData.title.length > 30 ? '...' : ''),
      slug: formData.slug,
      excerpt: formData.excerpt.substring(0, 30) + (formData.excerpt.length > 30 ? '...' : ''),
      content: formData.content.substring(0, 30) + (formData.content.length > 30 ? '...' : ''),
      content_length: formData.content.length,
      featured_image: formData.featured_image,
      status: formData.status,
      seo_title: formData.seo_title,
      seo_description: formData.seo_description,
      seo_keywords: formData.seo_keywords,
      scheduled_at: formData.scheduled_at,
    });
    
    console.log("Content edited flag state:", contentEdited);
  }, [formData, contentEdited]);
  
  const [selectedItineraryIds, setSelectedItineraryIds] = useState<string[]>([]);
  const [initialItineraryIds, setInitialItineraryIds] = useState<string[]>([]);
  const [selectedPackingListId, setSelectedPackingListId] = useState<string | undefined>(undefined);
  const [initialPackingListId, setInitialPackingListId] = useState<string | undefined>(undefined);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [initialTagIds, setInitialTagIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("content");
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    console.log(`Form field changed: ${name} = ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}`);
    
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      console.log(`Updated ${name} from "${prev[name as keyof BlogPostFormData]?.toString()?.substring(0, 20)}..." to "${value.substring(0, 20)}..."`);
      return updated;
    });
    
    // Auto-generate slug from title if slug is empty
    if (name === 'title' && !formData.slug) {
      const slug = value.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }

    // Auto-populate SEO title from title if seo_title is empty
    if (name === 'title' && !formData.seo_title) {
      setFormData(prev => ({ ...prev, seo_title: value }));
    }

    // Auto-populate SEO description from excerpt if seo_description is empty
    if (name === 'excerpt' && !formData.seo_description) {
      setFormData(prev => ({ ...prev, seo_description: value }));
    }
  };

  const handleSelectChange = (name: string, value: ContentStatus) => {
    console.log(`Select changed: ${name} = ${value}`);
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      console.log(`Updated ${name} from "${prev[name as keyof BlogPostFormData]}" to "${value}"`);
      return updated;
    });
  };

  const handleImageUploaded = (imagePath: string) => {
    console.log("Image uploaded, new path:", imagePath);
    setFormData(prev => {
      console.log(`Updating featured_image from "${prev.featured_image}" to "${imagePath}"`);
      const updatedData = { ...prev, featured_image: imagePath };
      console.log("Updated form data with new image:", updatedData);
      return updatedData;
    });
  };

  const handleDateTimeChange = (dateTime: Date | undefined) => {
    console.log("DateTime changed:", dateTime);
    setFormData(prev => {
      const newScheduledAt = dateTime ? dateTime.toISOString() : null;
      console.log(`Updating scheduled_at from "${prev.scheduled_at}" to "${newScheduledAt}"`);
      
      return { 
        ...prev, 
        scheduled_at: newScheduledAt,
        // Automatically set status to scheduled if a date is selected
        status: dateTime ? 'scheduled' : prev.status
      };
    });
  };
  
  const handleContentChange = (html: string) => {
    console.log("Content changed in RichTextEditor, length:", html.length);
    console.log("Content sample:", html.substring(0, 50) + (html.length > 50 ? '...' : ''));
    
    // IMPORTANT: Always mark content as edited when this function is called
    setContentEdited(true);
    console.log("Setting contentEdited flag to true due to explicit content edit");
    
    // Always update the content in form data
    setFormData(prev => {
      console.log(`Updating content from length ${prev.content.length} to length ${html.length}`);
      return { ...prev, content: html };
    });
  };

  // Check if form data has changed from initial data
  const hasFormChanged = () => {
    if (!initialFormData) {
      console.log("No initial form data, considering form changed");
      return true; // If no initial data, assume changed
    }
    
    // IMPORTANT: If the contentEdited flag is true, always consider the form changed
    if (contentEdited) {
      console.log("Content explicitly marked as edited, considering form changed");
      return true;
    }
    
    // Check for changes in form fields
    const hasFieldChanged = (
      initialFormData.title !== formData.title ||
      initialFormData.slug !== formData.slug ||
      initialFormData.excerpt !== formData.excerpt ||
      initialFormData.featured_image !== formData.featured_image ||
      initialFormData.status !== formData.status ||
      initialFormData.seo_title !== formData.seo_title ||
      initialFormData.seo_description !== formData.seo_description ||
      initialFormData.seo_keywords !== formData.seo_keywords ||
      initialFormData.scheduled_at !== formData.scheduled_at
    );
    
    // Check for changes in tags
    const hasTagsChanged = JSON.stringify([...selectedTagIds].sort()) !== JSON.stringify([...initialTagIds].sort());
    
    // Check for changes in itineraries
    const hasItinerariesChanged = JSON.stringify([...selectedItineraryIds].sort()) !== JSON.stringify([...initialItineraryIds].sort());
    
    // Check for changes in packing list
    const hasPackingListChanged = selectedPackingListId !== initialPackingListId;
    
    const hasChanged = hasFieldChanged || hasTagsChanged || hasItinerariesChanged || hasPackingListChanged;
    
    console.log("Field-by-field comparison result:", {
      hasFieldChanged,
      hasTagsChanged,
      hasItinerariesChanged,
      hasPackingListChanged,
      contentEdited,
      overall: hasChanged
    });
    
    // Return true if any changes detected
    return hasChanged || contentEdited;
  };

  return {
    formData,
    setFormData,
    initialFormData,
    setInitialFormData,
    selectedItineraryIds,
    setSelectedItineraryIds,
    initialItineraryIds,
    setInitialItineraryIds,
    selectedPackingListId,
    setSelectedPackingListId,
    initialPackingListId,
    setInitialPackingListId,
    selectedTagIds,
    setSelectedTagIds,
    initialTagIds,
    setInitialTagIds,
    activeTab,
    setActiveTab,
    loading,
    setLoading,
    handleChange,
    handleSelectChange,
    handleImageUploaded,
    handleDateTimeChange,
    handleContentChange,
    hasFormChanged,
    contentEdited,
    setContentEdited,
  };
}
